// src/pages/InvitationPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { acceptInvitation } from '../services/userManagementService';
import LandingHeader from '../components/LandingHeader';
import LandingFooter from '../components/LandingFooter';
import LoadingSpinner from '../components/LoadingSpinner';
import '../styles/pages/InvitationPage.scss';

const InvitationPage = () => {
    const { accountId, invitationId } = useParams();
    const { currentUser, login } = useAuth();
    const navigate = useNavigate();

    const [invitation, setInvitation] = useState(null);
    const [loading, setLoading] = useState(true);
    const [accepting, setAccepting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        loadInvitation();
    }, [accountId, invitationId]);

    const loadInvitation = async () => {
        try {
            setLoading(true);
            setError('');

            if (!accountId || !invitationId) {
                throw new Error('無効な招待リンクです');
            }

            // デバッグ情報をコンソールに出力

            const invitationRef = doc(db, 'accounts', accountId, 'invitations', invitationId);

            const invitationDoc = await getDoc(invitationRef);

            if (!invitationDoc.exists()) {
                throw new Error('招待が見つかりません');
            }

            const invitationData = invitationDoc.data();

            // 有効期限のチェック
            if (invitationData.expiresAt.toDate() < new Date()) {
                throw new Error('招待の有効期限が切れています');
            }

            // ステータスのチェック
            if (invitationData.status !== 'pending') {
                throw new Error('この招待は既に処理済みです');
            }

            setInvitation({
                id: invitationDoc.id,
                ...invitationData
            });

        } catch (error) {
            console.error('招待情報の取得に失敗:', error);
            let errorMessage = error.message;

            // Firestoreのアクセス権限エラーの場合の詳細メッセージ
            if (error.code === 'permission-denied') {
                errorMessage = '招待情報へのアクセス権限がありません。URLが正しいか確認してください。';
            } else if (error.code === 'not-found') {
                errorMessage = '招待が見つかりません。URLが正しいか確認してください。';
            }

            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleAcceptInvitation = async () => {
        if (!currentUser) {
            // ログインしていない場合は、ログインページにリダイレクト
            localStorage.setItem('pendingInvitation', JSON.stringify({
                accountId,
                invitationId
            }));
            navigate('/login');
            return;
        }

        try {
            setAccepting(true);
            setError('');

            // メールアドレスの確認
            if (currentUser.email !== invitation.email) {
                throw new Error('招待されたアカウントと異なるアカウントでログインしています');
            }

            await acceptInvitation(accountId, invitationId, {
                uid: currentUser.uid,
                email: currentUser.email,
                displayName: currentUser.displayName || currentUser.email
            });

            setSuccess('招待を受け入れました！ダッシュボードに移動します...');

            // 3秒後にダッシュボードにリダイレクト
            setTimeout(() => {
                navigate(`/dashboard/${accountId}`);
            }, 3000);

        } catch (error) {
            console.error('招待の受け入れに失敗:', error);
            setError(error.message);
        } finally {
            setAccepting(false);
        }
    };

    const formatRole = (role) => {
        return role === 'admin' ? '管理ユーザー' : '編集ユーザー';
    };

    const formatDate = (timestamp) => {
        return timestamp.toDate().toLocaleDateString('ja-JP');
    };

    if (loading) {
        return (
            <div>
                <LandingHeader showNavButton={false} />
                <main className="invitation-page">
                    <div className="container">
                        <LoadingSpinner />
                    </div>
                </main>
                <LandingFooter />
            </div>
        );
    }

    if (error) {
        return (
            <div>
                <LandingHeader />
                <main className="invitation-page">
                    <div className="container">
                        <div className="invitation-error">
                            <h2>招待エラー</h2>
                            <p>{error}</p>
                            <button
                                onClick={() => navigate('/')}
                                className="btn-primary"
                            >
                                ホームに戻る
                            </button>
                        </div>
                    </div>
                </main>
                <LandingFooter />
            </div>
        );
    }

    return (
        <div>
            <LandingHeader showNavButton={false} />
            <main className="invitation-page">
                <div className="container">
                    <div className="invitation-content">
                        <div className="invitation-header">
                            <h1>🎉 アカウントへの招待</h1>
                            <p>Suisui アカウントへの参加招待が届いています</p>
                        </div>

                        {success ? (
                            <div className="success-message">
                                <h2>✅ {success}</h2>
                            </div>
                        ) : (
                            <div className="invitation-details">
                                <div className="invitation-card">
                                    <h3>招待詳細</h3>
                                    <div className="detail-item">
                                        <label>ロール:</label>
                                        <span className={`role-badge ${invitation.role}`}>
                                            {formatRole(invitation.role)}
                                        </span>
                                    </div>
                                    <div className="detail-item">
                                        <label>有効期限:</label>
                                        <span>{formatDate(invitation.expiresAt)}</span>
                                    </div>
                                </div>

                                <div className="invitation-actions">
                                    {!currentUser ? (
                                        <div className="login-prompt">
                                            <p>招待を受け入れるには、まずログインしてください</p>
                                            <button
                                                onClick={() => navigate('/login')}
                                                className="btn-primary"
                                            >
                                                ログイン
                                            </button>
                                        </div>
                                    ) : currentUser.email !== invitation.email ? (
                                        <div className="email-mismatch">
                                            <p>⚠️ 招待されたアカウントと異なるアカウントでログインしています</p>
                                            <p>正しいアカウントでログインし直してください</p>
                                            <button
                                                onClick={() => navigate('/login')}
                                                className="btn-secondary"
                                            >
                                                別のアカウントでログイン
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="accept-invitation">
                                            <p>この招待を受け入れて、アカウントに参加しますか？</p>
                                            <button
                                                onClick={handleAcceptInvitation}
                                                disabled={accepting}
                                                className="btn-primary"
                                            >
                                                {accepting ? '処理中...' : '招待を受け入れる'}
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {error && (
                                    <div className="error-message">
                                        <p>❌ {error}</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </main>
            <LandingFooter />
        </div>
    );
};

export default InvitationPage;
